import type { BuiltInPagePlacementSection } from '@erp/rpc';
import type {
  RouterNavEntry,
  SidebarNavGroup,
} from '@/routing/nav-from-router';
import type {
  PageSectionNavGroup,
  TaskPageNavItem,
} from '@/routing/task-pages-nav';

/**
 * Pure logic for built-in module pages an org listed in one of its own page
 * sections (`PageSection.builtInPages`). Kept free of React/query imports
 * so it stays trivially unit-testable (mirrors task-pages-nav.ts).
 */

/**
 * Page items for every built-in entry an org section lists, ready for
 * {@link mergeTaskPagesIntoNav}. Additive: the entry also stays in its
 * route-derived group, and a page listed in several sections appears in each.
 * Entries are looked up in the already access-filtered `sections`, so a
 * placement never surfaces a page the viewer couldn't open.
 *
 * A label override replaces the page's name in that section only.
 *
 * Built-in pages lead their section in the order the admin listed them
 * (negative sort orders put them ahead of task/agent pages, whose sort
 * orders are never negative).
 */
export function placedBuiltInPageItems(
  sections: readonly SidebarNavGroup[],
  placements: readonly BuiltInPagePlacementSection[],
): TaskPageNavItem[] {
  if (placements.length === 0) return [];
  const entryByPath = new Map<string, RouterNavEntry>();
  for (const group of sections) {
    for (const entry of group.items) {
      if (!entryByPath.has(entry.fullPath)) {
        entryByPath.set(entry.fullPath, entry);
      }
    }
  }
  return placements.flatMap((placement) => {
    const count = placement.builtInPages.length;
    return placement.builtInPages.flatMap((page, index) => {
      const entry = entryByPath.get(page.path);
      if (!entry) return [];
      return [
        {
          entry: withLabelOverride(entry, page.label),
          navGroup: null,
          pageSectionId: placement.id,
          sortOrder: index - count,
        },
      ];
    });
  });
}

/**
 * The entry as it renders in an org section: the section's label override,
 * when set, replaces the route's own nav label. The override is org data, so
 * it is a plain string the label decoder passes through untranslated.
 */
function withLabelOverride(
  entry: RouterNavEntry,
  label: string | null,
): RouterNavEntry {
  if (label === null) return entry;
  return {
    ...entry,
    meta: { ...entry.meta, nav: { ...entry.meta.nav, label } },
  };
}

/**
 * Org sections known to the sidebar from either source — the full sections
 * list (fetched for task/agent pages) and the built-in placements — once each.
 */
export function unionPageSections(
  ...lists: ReadonlyArray<readonly PageSectionNavGroup[]>
): PageSectionNavGroup[] {
  const byId = new Map<string, PageSectionNavGroup>();
  for (const list of lists) {
    for (const section of list) {
      if (!byId.has(section.id)) byId.set(section.id, section);
    }
  }
  return [...byId.values()];
}
